import React, { useState, useEffect, useMemo } from "react";
import {
    ComposableMap,
    Geographies,
    Geography,
    Marker,
    ZoomableGroup,
} from "react-simple-maps";
import { scaleSqrt } from "d3-scale";
import { Box, Tooltip, Typography } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const CityDemographics = ({ data }) => {
    const [cityCoordinates, setCityCoordinates] = useState({});
    const [selectedCity, setSelectedCity] = useState(null);

    // Get cityData from props
    let cityData = useMemo(
        () => data?.raw_data?.total_value?.breakdowns[0]?.results,
        [data]
    );

    // Computing maximum value for city scaling (moved inside component)
    const maxValue = Math.max(...cityData?.map((d) => d.value || 0));
    const radiusScale = scaleSqrt()
        .domain([0, maxValue || 1])
        .range([2, 15]);

    // Fetch coordinates for each city using Geoapify API
    useEffect(() => {
        const fetchCoordinates = async () => {
            const coordinates = {};
            await Promise.all(
                cityData.map(async (item) => {
                    const cityName = item.dimension_values[0];
                    if (!coordinates[cityName]) {
                        try {
                            const response = await fetch(
                                `https://api.geoapify.com/v1/geocode/search?text=${encodeURIComponent(
                                    cityName
                                )}&apiKey=ac32a23d8d3b4dcd945bc3c3068801ac`
                            );
                            const data = await response.json();
                            if (data.features && data.features.length > 0) {
                                coordinates[cityName] =
                                    data.features[0].geometry.coordinates;
                            }
                        } catch (error) {
                            console.error(
                                "Error fetching coordinates for",
                                cityName,
                                error
                            );
                        }
                    }
                })
            );
            setCityCoordinates(coordinates);
        };

        if (cityData.length > 0) {
            fetchCoordinates();
        }
    }, [cityData]);

    return (
        <Box>
            <Box
                display={"flex"}
                alignItems={"center"}
                gap={0.5}
                margin={"3rem 0 2rem 0"}
            >
                <Typography variant="h6">City Demographics</Typography>

                <Tooltip title={data.raw_data.description} arrow>
                    <InfoIcon fontSize="small" sx={{ margin: 0 }} />
                </Tooltip>
            </Box>
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: "max-content",
                }}
                border={"1px solid #ccc"}
            >
                <ComposableMap projectionConfig={{ scale: 1100 }}>
                    <ZoomableGroup center={[78.9629, 22.5937]} zoom={1}>
                        <Geographies geography={geoUrl}>
                            {({ geographies }) =>
                                geographies.map((geo) => (
                                    <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        fill="#EEE"
                                        stroke="#DDD"
                                    />
                                ))
                            }
                        </Geographies>

                        {cityData.map((d, i) => {
                            const cityName = d.dimension_values[0];
                            const coordinates = cityCoordinates[cityName];
                            if (!coordinates) return null;

                            return (
                                <Marker
                                    key={i}
                                    coordinates={coordinates}
                                    onClick={() =>
                                        setSelectedCity({
                                            city: cityName,
                                            value: d.value,
                                        })
                                    }
                                    style={{ cursor: "pointer" }}
                                >
                                    <circle
                                        r={radiusScale(d.value)}
                                        fill="rgba(255,0,0,0.5)"
                                        stroke="#fff"
                                        strokeWidth={1}
                                    >
                                        <title>{`${cityName}: ${d.value}`}</title>
                                    </circle>
                                    <text
                                        textAnchor="middle"
                                        y={-10}
                                        style={{
                                            fontFamily: "system-ui",
                                            fill: "#5D5A6D",
                                            fontSize: 10,
                                        }}
                                    >
                                        {cityName}
                                    </text>
                                </Marker>
                            );
                        })}
                    </ZoomableGroup>
                </ComposableMap>

                {/* display selected city information */}
                {selectedCity && (
                    <div
                        style={{
                            position: "absolute",
                            top: "1rem",
                            left: "1rem",
                            background: "rgba(31,41,55,0.9)",
                            padding: "1rem",
                            borderRadius: "0.5rem",
                            boxShadow: "0 0 10px rgba(0,0,0,0.3)",
                            color: "white",
                            zIndex: 10,
                        }}
                    >
                        <h3 style={{ margin: 0 }}>{selectedCity.city}</h3>
                        <p style={{ margin: 0, fontWeight: "bold" }}>
                            {selectedCity.value.toLocaleString()}
                        </p>
                    </div>
                )}
            </Box>
        </Box>
    );
};

export default CityDemographics;
